/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'

type RegistrationSetupSkeletonProps = {
  /** Stack vertically (mobile), mirroring the modal's own responsive layout. */
  stacked?: boolean
  /** Sidebar placeholder rows — defaults to the modal's tab count. */
  tabCount?: number
}

const FIELD_ROWS = 4
const FIELDS_PER_ROW = 2

/**
 * Loading placeholder for the Registration Setup modal.
 *
 * Mirrors the real layout (tab rail + two-column form) so swapping in the
 * loaded content does not shift anything. Follows the conventions established
 * by PlayerFormSkeleton in the Edit Player modal.
 */
const RegistrationSetupSkeleton: React.FC<RegistrationSetupSkeletonProps> = ({
  stacked = false,
  tabCount = 7,
}) => {
  return (
    <div
      css={css`
        ${bodyStyle}
        flex-direction: ${stacked ? 'column' : 'row'};
      `}
      data-testid='registration-setup-skeleton'
      aria-hidden='true'
    >
      <div
        css={css`
          ${stacked ? mobileTabRowStyle : sidebarColStyle}
        `}
      >
        {Array.from({ length: tabCount }).map((_, index) => (
          <div key={index} css={stacked ? mobileTabStyle : sidebarItemStyle} />
        ))}
      </div>

      <div css={contentColStyle}>
        <div css={sectionHeaderBarStyle} />
        {Array.from({ length: FIELD_ROWS }).map((_, row) => (
          <div key={row} css={fieldRowStyle}>
            {Array.from({ length: FIELDS_PER_ROW }).map((_, field) => (
              <div key={field} css={fieldStyle}>
                <div css={labelBarStyle} />
                <div css={inputBarStyle} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

export default RegistrationSetupSkeleton

// ---- styled ----
const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const bar = css`
  border-radius: 8px;
  background-color: ${colors.SECONDARY_300};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const bodyStyle = css`
  display: flex;
  flex: 1;
  gap: 24px;
  overflow: hidden;
`

const sidebarColStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 250px;
  min-width: 250px;
  padding-right: 16px;
`

const sidebarItemStyle = css`
  ${bar}
  height: 40px;
`

const mobileTabRowStyle = css`
  display: flex;
  gap: 8px;
  overflow-x: hidden;
  padding-bottom: 8px;
`

const mobileTabStyle = css`
  ${bar}
  height: 36px;
  min-width: 96px;
`

const contentColStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
`

const sectionHeaderBarStyle = css`
  ${bar}
  height: 20px;
  width: 180px;
`

const fieldRowStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const labelBarStyle = css`
  ${bar}
  height: 16px;
  width: 120px;
`

const inputBarStyle = css`
  ${bar}
  height: 40px;
  width: 100%;
`
